import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

import {
  Attachment,
  ChatPostAttachment,
  ContractDocumentAttachment,
  DashboardTileAttachment,
  PersonAttachment,
  PlanLayoutAttachment,
  ProductionAttachment,
  ResourceAttachment,
  ResourceObjectAttachment,
  RoleJobStepAttachment,
} from "../@client/Theasoft_Data_Models"
import {
  ChatPostAttachmentMetadata,
  PartialContractConfirmationAttachment,
  PartialDashboardTileAttachment,
  PartialPersonAttachment,
  PartialPlanLayoutAttachment,
  PartialProductionAttachment,
} from "../@client/Theasoft_ApiServer_Attachments"
import { get } from "../typed-client/get"
import { GET } from "../@client/api"
import { PartialRoleJobStepAttachment } from "../@client/Theasoft_ApiServer_Roles_RoleJobSteps"
import { DefaultError } from "../-components/error-context"
import { parseProblemDetails } from "../utils/parse-problem-details"
import {
  PartialResourceAttachment,
  PartialResourceObjectAttachment,
} from "../@client/Theasoft_ApiServer_Persons_Attachments"

// Usually we use a auto generated client to access the api
// However combining file uploads including metadata is not yet supported by the client generation
// so we type the api manually here

// How to extend the AttachmentAPI with new AttachmentControllers:
// (1. If you haven't already: find README_HOW_TO_ADD_NEW_ATTACHMENTS.txt in the backend and follow instructions)
//  2. Add a new entry to the AttachmentControllerRouteToMetadataTypesMap below these instructions. There is an example that you can duplicate inside the map.
//  3. That's it. You can now use AttachmentUploadButton to upload your new Attachment.

export const AttachmentControllerRouteToMetadataTypesMap = {
  // Duplicate the following lines and fill in your specific stuff:
  // [yourSpecificApiRoutePart]: {
  //   partialMetadata: {} as Partial<[YourSpecificPartialAttachmentType]>,
  //   metadata: {} as [YourSpecificCompleteAttachmentType]
  // },
  persons: {
    partialMetadata: {} as Partial<PartialPersonAttachment>,
    metadata: {} as PersonAttachment,
  },
  productions: {
    partialMetadata: {} as Partial<PartialProductionAttachment>,
    metadata: {} as ProductionAttachment,
  },
  "role-job-steps": {
    partialMetadata: {} as Partial<PartialRoleJobStepAttachment>,
    metadata: {} as RoleJobStepAttachment,
  },
  dashboardtile: {
    partialMetadata: {} as Partial<PartialDashboardTileAttachment>,
    metadata: {} as DashboardTileAttachment,
  },
  chatpost: {
    partialMetadata: {} as Partial<ChatPostAttachmentMetadata>,
    metadata: {} as ChatPostAttachment,
  },
  planLayout: {
    partialMetadata: {} as Partial<PartialPlanLayoutAttachment>,
    metadata: {} as PlanLayoutAttachment,
  },
  "contracts/contract-documents": {
    partialMetadata: {} as Partial<PartialContractConfirmationAttachment>,
    metadata: {} as ContractDocumentAttachment,
  },
  resources: {
    partialMetadata: {} as Partial<PartialResourceAttachment>,
    metadata: {} as ResourceAttachment,
  },
  "resource-objects": {
    partialMetadata: {} as Partial<PartialResourceObjectAttachment>,
    metadata: {} as ResourceObjectAttachment,
  },
  chats: {
    partialMetadata: {},
    metadata: {} as Attachment,
  },
}

export type AttachmentMetadataTypeOfRoute<
  ControllerRoute extends AttachmentControllerRoute,
> =
  (typeof AttachmentControllerRouteToMetadataTypesMap)[ControllerRoute]["metadata"]

export type AttachmentControllerRoute =
  keyof typeof AttachmentControllerRouteToMetadataTypesMap

async function parseErrorDetailsAndThrow(response: Response) {
  const problemDetails = await parseProblemDetails(response)
  if (problemDetails !== null) {
    throw new DefaultError(problemDetails)
  } else {
    throw new Error("Unknown error. Status = " + response.status)
  }
}

export function useDefaultAttachmentsApiForControllerRoute<
  ControllerRoute extends AttachmentControllerRoute,
>(attachmentControllerRoute: ControllerRoute) {
  const baseUrl = `/api/${attachmentControllerRoute}/attachments`

  const queryClient = useQueryClient()

  return {
    baseUrl,

    useGetAttachmentsForContainer: function (containerId: number) {
      return useSuspenseQuery({
        retry: false,
        queryKey: ["attachments", containerId],
        queryFn: () =>
          // as necessary because template strings in urls arent supported
          {
            return get(`${baseUrl}/get-attachments` as keyof GET, {
              attachmentContainerId: containerId,
            }) as Promise<
              Array<
                (typeof AttachmentControllerRouteToMetadataTypesMap)[ControllerRoute]["metadata"]
              >
            >
          },
      })
    },

    getThumbnailUrl: function (
      attachmentId: number,
      containerId: number,
      pixelSize: number,
      portrait: boolean
    ) {
      return `${baseUrl}/thumbnail/?attachmentId=${attachmentId}&AttachmentContainerId=${containerId}&pixelSize=${pixelSize}&portrait=${portrait}`
    },

    updateAttachmentMetadata: async function (
      attachmentId: number,
      containerId: number,
      metadata: (typeof AttachmentControllerRouteToMetadataTypesMap)[ControllerRoute]["partialMetadata"]
    ) {
      const response = await fetch(`${baseUrl}/update-attachment-metadata`, {
        headers: {
          "Content-Type": "application/json",
          AttachmentId: attachmentId.toString(),
          AttachmentContainerId: containerId.toString(),
        },
        method: "POST",
        body: JSON.stringify(metadata),
      })

      if (response.ok) {
        queryClient.invalidateQueries({
          queryKey: ["attachments", containerId],
        })
        return response
      } else {
        await parseErrorDetailsAndThrow(response)
      }
    },

    addAttachmentAndMetadata: async function (
      file: File,
      metadata: (typeof AttachmentControllerRouteToMetadataTypesMap)[ControllerRoute]["partialMetadata"],
      containerId: number
    ) {
      const formData = new FormData()
      formData.append("file", file, file.name)
      formData.append("metadata", JSON.stringify(metadata))
      const response = await fetch(
        `${baseUrl}/add-attachment-file-and-metadata`,
        {
          headers: { attachmentContainerId: containerId.toString() },
          body: formData,
          method: "POST",
        }
      )
      if (response.ok) {
        return response
      } else {
        await parseErrorDetailsAndThrow(response)
      }
    },

    deleteAttachmentAndMetadata: async function (
      attachmentId: number,
      containerId: number
    ) {
      const response = await fetch(`${baseUrl}/delete-attachment`, {
        headers: {
          "Content-Type": "application/json",
          AttachmentId: attachmentId.toString(),
          AttachmentContainerId: containerId.toString(),
        },
        method: "POST",
        body: "{}",
      })
      if (response.ok) {
        queryClient.invalidateQueries({
          queryKey: ["attachments", containerId],
        })
        return response
      } else {
        await parseErrorDetailsAndThrow(response)
      }
    },

    getFileUrl: function (attachmentId: number, containerId: number) {
      return `${baseUrl}/get-file/?attachmentId=${attachmentId}&AttachmentContainerId=${containerId}`
    },
  }
}
